import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { Header } from "@/components/Header";
import { PageSeo } from "@/components/PageSeo";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";

const Auth = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, loading, login, signup } = useAuth();
  const { toast } = useToast();
  const { buildPath } = useLocalizedPath();
  const currentLang = i18n.language?.split('-')[0] || 'en';
  const [isLoading, setIsLoading] = useState(false);

  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });

  const [signupData, setSignupData] = useState({
    name: "",
    email: "",
    password: "",
    lang: currentLang,
  });

  // Redirect if already logged in
  useEffect(() => {
    if (!loading && user) {
      navigate(buildPath("/estyling"));
    }
  }, [user, loading, navigate, buildPath]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast({
        title: t('auth.welcomeBack'),
        description: t('auth.loggedIn'),
      });
      navigate(buildPath("/estyling"));
    } catch (error: any) {
      const status = error.response?.status;
      let errorTitle = t('auth.loginFailed');
      let errorDescription = error.message || t('auth.checkCredentials');
      
      if (status === 403) {
        // Obekräftad e-post: skicka om bekräftelsemejlet automatiskt
        try { await api.resendConfirmation(loginData.email); } catch {}
        errorTitle = t('auth.emailNotConfirmed');
        errorDescription = t('auth.emailNotConfirmedResent');
      } else if (status === 401) {
        errorTitle = t('auth.invalidCredentials');
        errorDescription = t('auth.wrongEmailPassword');
      } else if (status === 403) {
        errorTitle = t('auth.emailNotConfirmed');
        errorDescription = t('auth.confirmFirst');
      }
      
      toast({
        title: errorTitle,
        description: errorDescription,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (signupData.password.length < 8) {
      toast({
        title: t('auth.invalidPassword'),
        description: t('auth.passwordMinLength'),
        variant: "destructive",
      });
      return;
    }
    setIsLoading(true);
    try {
      const response = await signup(signupData.email, signupData.name, signupData.password, signupData.lang);
      toast({
        title: t('auth.accountCreated'),
        description: response.message || t('auth.confirmEmail'),
      });
      // Nollställ formulär
      setSignupData({ name: "", email: "", password: "", lang: currentLang });
    } catch (error: any) {
      toast({
        title: t('auth.signupFailed'),
        description: error.message || t('auth.tryDifferentEmail'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PageSeo />
      
      <Header />

      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">{t('auth.welcome')}</CardTitle>
            <CardDescription>
              {t('auth.subtitle')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">{t('auth.login')}</TabsTrigger>
                <TabsTrigger value="signup">{t('auth.signup')}</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">{t('auth.email')}</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder={t('auth.emailPlaceholder')}
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">{t('auth.password')}</Label>
                    <Input
                      id="login-password"
                      type="password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('auth.loggingIn')}
                      </>
                    ) : (
                      t('auth.login')
                    )}
                  </Button>
                  <div className="text-center mt-2">
                    <Link to={buildPath('/forgot-password')} className="text-sm text-primary hover:underline">
                      {t('auth.forgotPassword')}
                    </Link>
                  </div>
                </form>
              </TabsContent>

              <TabsContent value="signup">
                <form onSubmit={handleSignup} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">{t('auth.name')}</Label>
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder={t('auth.namePlaceholder')}
                      value={signupData.name}
                      onChange={(e) => setSignupData({ ...signupData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">{t('auth.email')}</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder={t('auth.emailPlaceholder')}
                      value={signupData.email}
                      onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">{t('auth.password')}</Label>
                    <Input
                      id="signup-password"
                      type="password"
                      placeholder={t('auth.passwordPlaceholder')}
                      value={signupData.password}
                      onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-lang">{t('profile.language')}</Label>
                    <Select
                      value={signupData.lang}
                      onValueChange={(value) => setSignupData({ ...signupData, lang: value })}
                    >
                      <SelectTrigger id="signup-lang">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sv">🇸🇪 Svenska</SelectItem>
                        <SelectItem value="en">🇬🇧 English</SelectItem>
                        <SelectItem value="da">🇩🇰 Dansk</SelectItem>
                        <SelectItem value="no">🇳🇴 Norsk</SelectItem>
                        <SelectItem value="fi">🇫🇮 Suomi</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('auth.creatingAccount')}
                      </>
                    ) : (
                      t('auth.signup')
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
          <CardFooter className="text-center text-sm text-muted-foreground">
            <p className="w-full">
              {t('auth.privacyNotice')}{" "}
              <Link to={buildPath('/privacy')} className="text-primary hover:underline">
                {t('nav.privacy')}
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
